import { WEBXR_TO_NUE } from "../ar/webxr-nue-basis.js";
import * as THREE from "three";
//#region ../src/visualization/occupancy-cubes-visualizer.ts
/**
* Occupancy-cubes visualizer — renders the AR-space occupancy grid as one
* `THREE.InstancedMesh` of cubes (2026-06-11 depth occupancy-grid port
* plan §3/Iter 5). The TS equivalent of the Unity debug cubes in
* `ArCursorOnDepthSurface.cs`: refreshed by `wireOccupancyGridSubscribers`
* at the depth-sample interval (`depth.intervalMs`; was a fixed ~1 Hz
* before Issue A), drawing all occupied cells when under the instance cap
* and, above it, the cells nearest the viewer when a pose is supplied
* (Issue B1) — otherwise a random subset (the original "randomly picking
* points every second" behavior; no geometry-shader point billboard is
* ported, WebGL has none).
*
* Coloring: height-based per-instance color (HSL ramp over the cell's Y)
* until per-point RGB capture lands (plan §5).
*
* Coordinate space: the grid's cells are **raw WebXR** coordinates, but
* the AR-space node (`arWorldGroup`) they must ride lives in AR-odometry
* NUE. The mesh therefore carries the constant `WEBXR_TO_NUE` basis
* change as its own local matrix — the same role `basisChangeNode` plays
* for the camera — so every cube's world pose follows the camera's
* `alignment × WEBXR_TO_NUE` chain (see the hit-test-reticle entry in
* `GpsPlusSlamJs_Docs/docs/lessons-learned.md`). Parenting the mesh at
* the scene root instead leaves the cubes axis-swapped and unaligned.
*
* The parent node is injected explicitly (no `getArWorldGroup()` call)
* so the class stays unit-testable — same P3 rule as
* `FrameTileVisualizer`.
*/
const DEFAULT_MAX_INSTANCES = 2e3;
const DEFAULT_OVER_CAP_RADIUS_M = 10;
const DEFAULT_CUBE_SIZE_M = .025;
const MESH_NAME = "occupancy-cubes";
/** Height range mapped onto the color ramp (meters, raw WebXR Y). */
const COLOR_Y_MIN = -1;
const COLOR_Y_MAX = 3;
var OccupancyCubesVisualizer = class {
	arSpaceNode;
	minObservations;
	cubeSizeM;
	rng;
	overCapRadiusM;
	mesh;
	geometry;
	material;
	disposed = false;
	/**
	* @param arSpaceNode - the node whose local space is AR-odometry NUE
	*   and which receives the alignment matrix (`arWorldGroup` live,
	*   `replaySceneState.arWorldGroup` in replay) — NOT the scene root.
	*/
	constructor(arSpaceNode, options = {}) {
		this.arSpaceNode = arSpaceNode;
		this.minObservations = options.minObservations ?? 1;
		this.cubeSizeM = options.cubeSizeM ?? DEFAULT_CUBE_SIZE_M;
		this.rng = options.rng ?? Math.random;
		const radius = options.overCapRadiusM ?? DEFAULT_OVER_CAP_RADIUS_M;
		this.overCapRadiusM = Number.isFinite(radius) && radius > 0 ? radius : Infinity;
		const maxInstances = options.maxInstances ?? DEFAULT_MAX_INSTANCES;
		this.geometry = new THREE.BoxGeometry(1, 1, 1);
		this.material = new THREE.MeshBasicMaterial({
			transparent: true,
			opacity: .7
		});
		this.mesh = new THREE.InstancedMesh(this.geometry, this.material, maxInstances);
		this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
		this.mesh.name = MESH_NAME;
		this.mesh.count = 0;
		this.mesh.frustumCulled = false;
		this.mesh.matrixAutoUpdate = false;
		this.mesh.matrix.copy(WEBXR_TO_NUE);
		this.arSpaceNode.add(this.mesh);
	}
	/** Number of cubes currently drawn. */
	getCount() {
		return this.mesh.count;
	}
	/**
	* Show or hide the cubes live without discarding them — the cheap
	* mesh-view toggle (a later `refresh` still repopulates them). Flips the
	* instanced mesh's `visible` flag; O(1), no re-mesh.
	*/
	setVisible(visible) {
		this.mesh.visible = visible;
	}
	/**
	* Redraw from the grid: every sufficiently-observed cell when under the
	* instance cap. Over cap, draw the cells **nearest the viewer** when a
	* `viewerPose` is supplied (Issue B1 — keeps the local neighbourhood
	* dense instead of a room-wide random scatter); fall back to the legacy
	* random subset when no pose is given or the pose is non-finite (a
	* tracking glitch), so older callers and deterministic tests are
	* unchanged.
	*
	* Each chosen cell's draw position is computed exactly once (via
	* `getCellPoint` ?? `getCellCenter`) and carried through to the draw
	* loop — the over-cap ranking needs it one step earlier than the legacy
	* code did.
	*/
	refresh(grid, viewerPose) {
		if (this.disposed) return;
		const hasFinitePose = viewerPose !== void 0 && isFiniteVec3(viewerPose.cameraPos);
		const occupied = grid.getOccupiedCellsWithin && hasFinitePose && Number.isFinite(this.overCapRadiusM) ? grid.getOccupiedCellsWithin(viewerPose.cameraPos, this.overCapRadiusM, this.minObservations) : grid.getOccupiedCells(this.minObservations);
		const capacity = this.mesh.instanceMatrix.count;
		const positionOf = (cell) => grid.getCellPoint?.(cell) ?? grid.getCellCenter(cell);
		let drawn;
		if (occupied.length <= capacity) drawn = occupied.map((cell) => ({
			cell,
			pos: positionOf(cell)
		}));
		else if (viewerPose && isFiniteVec3(viewerPose.cameraPos)) drawn = pickNearestSubset(occupied, capacity, viewerPose.cameraPos, positionOf, this.overCapRadiusM).map(({ item, pos }) => ({
			cell: item,
			pos
		}));
		else drawn = pickRandomSubset(occupied, capacity, this.rng).map((cell) => ({
			cell,
			pos: positionOf(cell)
		}));
		const matrix = new THREE.Matrix4();
		const color = new THREE.Color();
		for (let i = 0; i < drawn.length; i++) {
			const placed = drawn[i];
			if (placed === void 0) continue;
			const [x, y, z] = placed.pos;
			matrix.makeScale(this.cubeSizeM, this.cubeSizeM, this.cubeSizeM);
			matrix.setPosition(x, y, z);
			this.mesh.setMatrixAt(i, matrix);
			const rgb = grid.getCellColor(placed.cell);
			this.mesh.setColorAt(i, rgb ? color.setRGB(rgb[0] / 255, rgb[1] / 255, rgb[2] / 255) : heightColor(color, y));
		}
		this.mesh.count = drawn.length;
		this.mesh.instanceMatrix.needsUpdate = true;
		if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
	}
	/** Hide all cubes (e.g. on store swap); the mesh stays in the scene. */
	clear() {
		this.mesh.count = 0;
	}
	/** Remove the mesh from its parent and release GPU resources. */
	dispose() {
		if (this.disposed) return;
		this.disposed = true;
		this.arSpaceNode.remove(this.mesh);
		this.mesh.dispose();
		this.geometry.dispose();
		this.material.dispose();
	}
};
/** Map cell height to a stable HSL ramp (blue floor → red ceiling). */
function heightColor(target, y) {
	const t = Math.min(1, Math.max(0, (y - COLOR_Y_MIN) / (COLOR_Y_MAX - COLOR_Y_MIN)));
	return target.setHSL(.66 * (1 - t), 1, .5);
}
/** True when every component of a 3-vector is a finite number. */
function isFiniteVec3(v) {
	return Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
}
/**
* Pick the `count` items nearest `eye`, ranked by squared distance to each
* item's draw position. Pure and deterministic: positions come from
* `positionOf` (computed exactly once per item) and are carried through on
* the result so the draw loop never re-fetches them. Ties keep input order
* (stable sort). Exported for the property tests in
* `occupancy-cubes-visualizer.property.test.ts`.
*
* Squared distance is sufficient — `sqrt` is monotonic, so it never changes
* the ranking, and skipping it avoids `count` square roots per repaint.
*
* `maxRadius` (Step 1.1 of the 2026-07-03 long-session fps plan) drops items
* farther than that from `eye` BEFORE scoring/sorting, shrinking the sort
* from O(N log N) over every cell ever seen to O(K log K) over the local
* neighbourhood — the linear-in-session-length term behind the long-walk fps
* decline. Behaviour-preserving whenever the count-th nearest item lies
* within the radius (property-tested); when fewer than `count` items survive
* the filter, only those are returned — distant cubes vanish by design (plan
* §Risks). Omitted / non-finite radius = unbounded (legacy behaviour).
*/
function pickNearestSubset(items, count, eye, positionOf, maxRadius) {
	const maxD2 = maxRadius !== void 0 && Number.isFinite(maxRadius) ? maxRadius * maxRadius : Infinity;
	const scored = [];
	for (const item of items) {
		const pos = positionOf(item);
		const dx = pos[0] - eye[0];
		const dy = pos[1] - eye[1];
		const dz = pos[2] - eye[2];
		const d2 = dx * dx + dy * dy + dz * dz;
		if (d2 <= maxD2) scored.push({
			item,
			pos,
			d2
		});
	}
	scored.sort((a, b) => a.d2 - b.d2);
	return scored.slice(0, Math.max(0, count)).map(({ item, pos }) => ({
		item,
		pos
	}));
}
/**
* Pick `count` distinct elements via partial Fisher–Yates on a copy —
* O(count), unbiased for an unbiased rng, deterministic for injected rngs.
*/
function pickRandomSubset(items, count, rng) {
	const pool = [...items];
	const result = [];
	const limit = Math.min(count, pool.length);
	for (let i = 0; i < limit; i++) {
		const j = i + Math.floor(rng() * (pool.length - i));
		const a = pool[i];
		const b = pool[j];
		if (a === void 0 || b === void 0) continue;
		pool[i] = b;
		pool[j] = a;
		result.push(b);
	}
	return result;
}
//#endregion
export { OccupancyCubesVisualizer, pickNearestSubset };
